import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import syncFill from '@iconify/icons-eva/sync-fill';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useTheme, experimentalStyled as styled, alpha } from '@material-ui/core/styles';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
  Collapse,
  IconButton,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import QbLogo from 'src/assets/quickbookLogo-removebg-preview.png';

// ----------------------------------------------------------------------

const getStatusPaletteKey = (status) => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  return 'warning';
};

const StockBox = styled(Box)(({ theme, variant, highlight }) => {
  const paletteKey =
    variant === 'software' ? 'info' : highlight === 'error' ? 'error' : 'warning';

  return {
    flex: '1 1 0%',
    minWidth: 0,
    width: '100%',
    height: '100%',
    minHeight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius * 1.5,
    border: `1px solid ${alpha(theme.palette[paletteKey].main, 0.24)}`,
    backgroundColor: alpha(
      theme.palette[paletteKey].main,
      theme.palette.mode === 'light' ? 0.08 : 0.16
    ),
    transition: theme.transitions.create(['box-shadow', 'border-color'], {
      duration: theme.transitions.duration.shorter,
    }),
  };
});

const CardsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  gap: theme.spacing(2),
  minWidth: 0,
  alignItems: 'stretch',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

function CardHeader({ title, icon }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25, minHeight: 28 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon || null}
      </Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, lineHeight: 1.3 }}>
        {title}
      </Typography>
    </Stack>
  );
}

CardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
};

const StatusCircle = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.success.main, 0.16),
  color: theme.palette.success.main,
  flexShrink: 0,
}));

const StatusBanner = styled(Box)(({ theme, status }) => {
  const paletteKey = getStatusPaletteKey(status);

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${alpha(theme.palette[paletteKey].main, 0.35)}`,
    backgroundColor: alpha(
      theme.palette[paletteKey].main,
      theme.palette.mode === 'light' ? 0.1 : 0.18
    ),
  };
});

const StatusIconWrap = styled(Box)(({ theme, status }) => {
  const paletteKey = getStatusPaletteKey(status);

  return {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: alpha(theme.palette[paletteKey].main, 0.2),
    color: theme.palette[paletteKey].main,
  };
});

const formatQbItemType = (type) => {
  if (!type) return 'Unknown';
  const labels = {
    Inventory: 'Inventory',
    NonInventory: 'Non-Inventory',
    Service: 'Service',
    Group: 'Bundle/Group',
    Category: 'Category',
  };
  return labels[type] || type;
};

function StockSyncStatus({ isMatched, loading, isQbLinked, isQbInventoryType, qbItemType }) {
  if (loading) {
    return null;
  }

  if (!isQbLinked) {
    return (
      <StatusBanner status="warning">
        <StatusIconWrap status="warning">
          <Icon icon={alertTriangleFill} width={22} height={22} />
        </StatusIconWrap>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            QuickBooks not linked
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Link this material to QuickBooks to compare and sync inventory.
          </Typography>
        </Box>
      </StatusBanner>
    );
  }

  if (!isQbInventoryType) {
    const typeLabel = formatQbItemType(qbItemType);
    return (
      <StatusBanner status="warning">
        <StatusIconWrap status="warning">
          <Icon icon={alertTriangleFill} width={22} height={22} />
        </StatusIconWrap>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Not an Inventory item in QuickBooks
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {`This product is linked as "${typeLabel}" in QuickBooks. Stock quantity sync is only available for Inventory type items. Change the item type to Inventory in QuickBooks to enable sync.`}
          </Typography>
        </Box>
      </StatusBanner>
    );
  }

  if (isMatched) {
    return (
      <StatusBanner status="success">
        <StatusIconWrap status="success">
          <Icon icon={checkmarkCircle2Fill} width={22} height={22} />
        </StatusIconWrap>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
            Stock is up to date
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Software and QuickBooks quantities are up to date.
          </Typography>
        </Box>
      </StatusBanner>
    );
  }

  return (
    <StatusBanner status="error">
      <StatusIconWrap status="error">
        <Icon icon={alertTriangleFill} width={22} height={22} />
      </StatusIconWrap>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
          Stock mismatch detected
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
          Software and QuickBooks stock quantities are not matching. Please sync now to update QuickBooks.
        </Typography>
      </Box>
    </StatusBanner>
  );
}

StockSyncStatus.propTypes = {
  isMatched: PropTypes.bool,
  loading: PropTypes.bool,
  isQbLinked: PropTypes.bool,
  isQbInventoryType: PropTypes.bool,
  qbItemType: PropTypes.string,
};

// ----------------------------------------------------------------------

const formatStock = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '0.00';
  return num.toFixed(2);
};

const formatPrice = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return `$ ${num.toFixed(2)}`;
};

const stocksMatch = (softwareStock, qbStock) =>
  Math.abs(Number(softwareStock) - Number(qbStock)) < 0.001;

function PriceDetails({ purchasePrice, salesPrice, loading, muted }) {
  return (
    <Stack spacing={0.25} sx={{ mt: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" sx={{ color: muted ? 'text.disabled' : 'text.secondary' }}>
          Purchase Cost
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: muted ? 'text.disabled' : 'text.primary' }}>
          {loading ? '…' : purchasePrice}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" sx={{ color: muted ? 'text.disabled' : 'text.secondary' }}>
          Sales Price
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: muted ? 'text.disabled' : 'text.primary' }}>
          {loading ? '…' : salesPrice}
        </Typography>
      </Stack>
    </Stack>
  );
}

PriceDetails.propTypes = {
  purchasePrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  salesPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  muted: PropTypes.bool,
};

// ----------------------------------------------------------------------

InventoryStockComparison.propTypes = {
  materialId: PropTypes.number,
  refreshData: PropTypes.any,
  embedded: PropTypes.bool,
};

export default function InventoryStockComparison({ materialId, refreshData, embedded = false }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stockData, setStockData] = useState({
    softwareStock: 0,
    qbStock: 0,
    syncWithQuickbooks: 0,
    name: '',
    softwarePurchasePrice: null,
    softwareSalesPrice: null,
    qbPurchasePrice: null,
    qbSalesPrice: null,
    qbItemType: null,
    isQbInventoryType: true,
  });

  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = userData?.user_id;

  const emptyStockData = {
    softwareStock: 0,
    qbStock: 0,
    syncWithQuickbooks: 0,
    name: '',
    softwarePurchasePrice: null,
    softwareSalesPrice: null,
    qbPurchasePrice: null,
    qbSalesPrice: null,
    qbItemType: null,
    isQbInventoryType: true,
  };

  const fetchStockDetails = useCallback(async () => {
    if (!materialId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${REST_API_END_POINT}products/get-material-stock-details`, {
        material_id: materialId,
      });

      if (res.data.status === 1 && res.data.result) {
        const { result } = res.data;
        const qbItemType = result.qbItemType ?? null;
        const isQbInventoryType =
          result.isQbInventoryType != null
            ? Boolean(result.isQbInventoryType)
            : qbItemType == null || qbItemType === 'Inventory';

        setStockData({
          softwareStock: result.softwareStock ?? 0,
          qbStock: result.qbStock ?? 0,
          syncWithQuickbooks: result.syncWithQuickbooks ?? 0,
          name: result.name ?? '',
          softwarePurchasePrice: result.softwarePurchasePrice ?? null,
          softwareSalesPrice: result.softwareSalesPrice ?? null,
          qbPurchasePrice: result.qbPurchasePrice ?? result.qb_purchase_cost ?? null,
          qbSalesPrice: result.QbSalesPrice ?? result.qb_sales_price ?? null,
          qbItemType,
          isQbInventoryType,
        });
      } else {
        setStockData(emptyStockData);
      }
    } catch (error) {
      console.error('Error fetching stock details:', error);
      setStockData(emptyStockData);
    } finally {
      setLoading(false);
    }
  }, [materialId]);

  useEffect(() => {
    fetchStockDetails();
  }, [fetchStockDetails, refreshData]);

  const {
    softwareStock,
    qbStock,
    syncWithQuickbooks,
    name,
    softwarePurchasePrice,
    softwareSalesPrice,
    qbPurchasePrice,
    qbSalesPrice,
    qbItemType,
    isQbInventoryType,
  } = stockData;
  const isMatched = stocksMatch(softwareStock, qbStock);
  const isQbLinked = Number(syncWithQuickbooks) === 1;
  const canSyncStock = isQbLinked && isQbInventoryType;
  const hasStockMismatch = canSyncStock && !isMatched;

  const handleSyncNow = async () => {
    if (!materialId) return;
    if (!isQbInventoryType) {
      enqueueSnackbar(
        `Cannot sync stock. This QuickBooks item is "${formatQbItemType(qbItemType)}", not Inventory.`,
        { variant: 'warning' }
      );
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.post(`${REST_API_END_POINT}products/sync-material-inventory-stock-to-qb`, {
        material_id: materialId,
        userId,
      });

      if (res.data.status === 1) {
        enqueueSnackbar(res.data.message || 'Stock synced successfully', { variant: 'success' });
        await fetchStockDetails();
      } else {
        enqueueSnackbar(res.data.message || 'Failed to sync stock', { variant: 'error' });
      }
    } catch (error) {
      console.error('Sync error:', error);
      enqueueSnackbar('Failed to sync stock to QuickBooks', { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const getSummaryLabel = () => {
    if (loading) return 'Loading stock comparison…';
    if (!isQbLinked) return 'QuickBooks not linked';
    if (!isQbInventoryType) {
      return `QB type: ${formatQbItemType(qbItemType)} — sync unavailable`;
    }
    if (isMatched) return 'Stock is up to date';
    return 'Stock mismatch — sync required';
  };

  if (!materialId) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: embedded ? '100%' : 'auto',
        px: embedded ? 2 : 3,
        py: embedded ? 1 : 2,
        ...(embedded
          ? {}
          : { borderBottom: `1px solid ${theme.palette.divider}` }),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: expanded ? 1.5 : 0 }}
      >
        <Tooltip title={expanded ? 'Minimize details' : 'Expand details'}>
          <IconButton
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Minimize stock comparison details' : 'Expand stock comparison details'}
            sx={{ color: 'text.secondary' }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Tooltip>
        {!expanded && (
          <Typography
            variant="caption"
            onClick={() => setExpanded(true)}
            sx={{
              color: !isQbLinked || !isQbInventoryType
                ? 'warning.main'
                : isMatched
                  ? 'success.main'
                  : 'error.main',
              cursor: 'pointer',
              flex: 1,
              minWidth: 0,
            }}
          >
            {getSummaryLabel()}
            {isQbLinked && isQbInventoryType && !loading && (
              <> · Software {formatStock(softwareStock)} · QB {formatStock(qbStock)}</>
            )}
          </Typography>
        )}
      </Stack>

      <Collapse in={expanded} timeout="auto">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
        spacing={2}
      >
        <CardsRow>
          <StockBox variant="software">
            <CardHeader title="Total Stock in Software" />
            <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center', mb: 0.25 }}>
              {loading ? (
                <CircularProgress size={22} color="info" />
              ) : (
                <Typography variant="h5" sx={{ color: 'info.main', fontWeight: 700, lineHeight: 1.2 }}>
                  {formatStock(softwareStock)}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <PriceDetails
                loading={loading}
                purchasePrice={softwarePurchasePrice}
                salesPrice={softwareSalesPrice}
              />
            </Box>
          </StockBox>

          <StockBox variant="qb" highlight={hasStockMismatch ? 'error' : undefined}>
            <CardHeader
              title="Total Stock in QuickBooks"
              icon={
                <Box
                  component="img"
                  src={QbLogo}
                  alt="QuickBooks"
                  sx={{ width: 24, height: 24, objectFit: 'contain' }}
                />
              }
            />
            <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center', mb: 0.25 }}>
              {loading ? (
                <CircularProgress size={22} color={hasStockMismatch ? 'error' : 'warning'} />
              ) : (
                <Typography
                  variant="h5"
                  sx={{
                    color: !isQbLinked || !isQbInventoryType
                      ? 'text.disabled'
                      : hasStockMismatch
                        ? 'error.main'
                        : 'warning.main',
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {!isQbLinked || !isQbInventoryType ? '—' : formatStock(qbStock)}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <PriceDetails
                loading={loading}
                muted={!isQbLinked}
                purchasePrice={isQbLinked ? qbPurchasePrice : null}
                salesPrice={isQbLinked ? qbSalesPrice : null}
              />
            </Box>
          </StockBox>
        </CardsRow>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: { xs: 'stretch', md: 'center' },
            minWidth: { md: 160 },
            flexShrink: 0,
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : !isQbLinked ? (
            <Tooltip title="This material is not linked to QuickBooks">
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Not synced with QB
              </Typography>
            </Tooltip>
          ) : !isQbInventoryType ? (
            <Tooltip
              title={`QuickBooks item type is ${formatQbItemType(qbItemType)}. Only Inventory items can sync stock.`}
            >
              <Typography variant="caption" sx={{ color: 'warning.main', textAlign: 'center', maxWidth: 140 }}>
                {formatQbItemType(qbItemType)}
                <br />
                Sync unavailable
              </Typography>
            </Tooltip>
          ) : isMatched ? (
            <Tooltip title="Software and QuickBooks stock match">
              <StatusCircle>
                <Icon icon={checkmarkCircle2Fill} width={32} height={32} />
              </StatusCircle>
            </Tooltip>
          ) : (
            <LoadingButton
              variant="contained"
              color="success"
              size="medium"
              loading={syncing}
              startIcon={<Icon icon={syncFill} width={20} height={20} />}
              onClick={handleSyncNow}
              sx={{
                px: 3,
                py: 1.25,
                fontWeight: 600,
                boxShadow: theme.customShadows?.z8,
              }}
            >
              Sync Now
            </LoadingButton>
          )}
        </Box>
      </Stack>
      </Collapse>
      {/* Always visible so non-inventory / mismatch reasons show in every usage (incl. minimized) */}
      <StockSyncStatus
        isMatched={isMatched}
        loading={loading}
        isQbLinked={isQbLinked}
        isQbInventoryType={isQbInventoryType}
        qbItemType={qbItemType}
      />
    </Box>
  );
}
